import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { LegalRestriction } from '@next/arable-lib/advice';
import { loadCrops } from '@next/arable-lib/crop';
import { loadGenericCropAids } from '@next/arable-lib/crop-aid';
import { CoreActions } from '@next/core-lib';
import { CartActions } from '@next/core-lib/cart';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { UserActions, UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, EMPTY, from, of } from 'rxjs';

import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  groupBy,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap,
  toArray,
  withLatestFrom,
} from 'rxjs/operators';
import { loadSoilTypes } from 'src/app/soil-type';
import { DateService } from 'src/app/shared/services/date.service';

import { Environment } from 'src/environments/environment.model';


import {
  addShouldRecalculate,
  checkUnsavedChanges,
  confirmLeaveWithUnsavedChanges,
  createSprayApplication,
  deleteSprayApplication,
  emergenceDateUpdated,
  harvestDateUpdated,
  loadBasicSprays,
  loadBasicSpraysFailure,
  loadBasicSpraysSuccess,
  loadCropFieldRegistration,
  loadCropFieldsForSpray,
  loadCustomerBlightRights,
  loadCustomerBlightRightsFailure,
  loadCustomerBlightRightsSuccess,
  loadLegalRestrictions,
  loadLegalRestrictionsFailure,
  loadLegalRestrictionsSuccess,
  loadSprayApplications,
  loadSprayApplicationsFailure,
  loadSprayApplicationsSuccess,
  openBlightIrrigation,
  openSpraysDialog,
  plantDateUpdated,
  removeShouldRecalculate,
  requestBlightRecalculation,
  requestCropFieldBlightDataByCropFieldId,
  setBlightAdviceDate,
  setBlightChartDetails,
  setBlightRecalculationStatusPerCropField,
  setCropFieldRegistration,
  setCropFieldsForSpray,
  setIsSpraysLoading,
  setSelectedBlightCropFieldIds,
  sprayApplicationCreated,
  updateCropFieldVisibilityStatus,
  updateCropFieldVisibilityStatusSuccess,
  updateEmergenceDate,
  updateHarvestDate,
  updatePhytophthoraPressure,
  updatePlantDate,
  updateSuppressSprayAdvice,
} from './actions';
import {
  isCropYearReadOnly,
  selectCropYear,
  selectSelectedBlightCropFieldIds,
  selectShouldRecalculateForCropFieldIds,
} from './selectors';
import { AdjustSpraysDialogComponent } from '../blight-v2/components/adjust-sprays-dialog/adjust-sprays-dialog.component';
import { IrrigationDialogComponent } from '@app/irrigations/components/irrigation-dialog/irrigation-dialog.component'
import { BlightChartDetail, CropFieldRegistration, CustomerBlightRights } from '../blight-v2/models/models';
import { BlightService } from '../blight-v2/services/blight.service';
import { loadBlightDataFailure, loadParticipatingCropFields } from '../blight-v2/store/actions';
import { AppState, BasicSpray, CreateSprayApplicationData, SprayApplication } from '../shared/models';
import { CropVarietyService } from '../shared/services/crop-variety.service';
import { CropYearService } from '../shared/services/crop-year.service';
import { UnsavedChangesService } from '../shared/services/unsaved-changes.service';
import { handleHttpErrorWithSnackbar } from '@app/shared/utils';
@Injectable()
export class AppEffects {
  protected store = inject<Store<AppState>>(Store);
  protected actions$ = inject(Actions);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly dateService = inject(DateService);
  private readonly blightService = inject(BlightService);
  private readonly cropVarietyService = inject(CropVarietyService);
  private readonly cropYearService = inject(CropYearService);
  private readonly router = inject(Router);
  private readonly unsavedChangesService = inject(UnsavedChangesService);
  private platformID = inject<Object>(PLATFORM_ID);
  readonly env = inject<Environment>(EnvironmentInjector);

  locationInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationInitialized),
      filter(() => isPlatformBrowser(this.platformID)),
      switchMap((_) => [loadCrops(), loadGenericCropAids(), loadSoilTypes(), CartActions.getCartsRequest({})]),
    ),
  );

  requestBlightChartDetailsByCropField$ = createEffect(() =>
    this.actions$.pipe(
      ofType(requestCropFieldBlightDataByCropFieldId),
      withLatestFrom(this.store.pipe(select(selectCropYear))),
      switchMap(([action, cropYear]) =>
        this.blightService.getBlightChartDetailsByCropField(cropYear, action.cropFieldId, action.startDate, action.endDate).pipe(
          switchMap((blightChartDetails: BlightChartDetail[]) => of(
            setBlightChartDetails({ blightChartDetails }),
            setBlightAdviceDate({ adviceDate: new Date(action.adviceDate) })),
          ),
          catchError(() =>
            of(
              NotificationActions.errorSnackbar({
                message: this.locale.translate('errors.blight.genericError'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadCustomerBlightRights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCustomerBlightRights),
      switchMap((_) =>
        combineLatest({
          cropYear: this.cropYearService.cropYear$,
          customerId: this.store.select(UserSelectors.selectCustomer).pipe(map((customer) => customer?.id)),
        }).pipe(
          distinctUntilChanged((previous, current) =>
            previous.customerId === current.customerId && previous.cropYear === current.cropYear),
          switchMap(({ customerId, cropYear }) => customerId
            ? this.blightService.getCustomerBlightRights(customerId, cropYear).pipe(
              switchMap((customerBlightRights: CustomerBlightRights) =>
                of(loadCustomerBlightRightsSuccess({ customerBlightRights })),
              ),
              catchError(() => of(loadCustomerBlightRightsFailure({ message: 'errors.blight.blightRightsError' }))),
            ) : EMPTY,
          ),
        ),
      ),
    ),
  );

  loadBasicSprays$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBasicSprays),
      switchMap(({ payload: { date } }) =>
        this.blightService.getActiveSprays(date).pipe(
          map((basicSprays: BasicSpray[]) => loadBasicSpraysSuccess({ basicSprays })),
          catchError(() =>
            of(loadBasicSpraysFailure({ message: 'errors.blight.failedToLoadBasicSprays' })),
          ),
        ),
      ),
    ),
  );

  createSprayApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createSprayApplication),
      tap(() => this.store.dispatch(setIsSpraysLoading({ isSpraysLoading: true }))),
      withLatestFrom(this.store.select(selectSelectedBlightCropFieldIds)),
      mergeMap(([action, selectedCropFieldIds]) =>
        from(selectedCropFieldIds).pipe(
          mergeMap((cropFieldId) => {
            const sprayApplication: CreateSprayApplicationData = {
              ...action.sprayApplication,
              cropFieldId,
            };

            return this.blightService.createSprayApplication(sprayApplication).pipe(
              tap(() => {
                this.store.dispatch(addShouldRecalculate({ cropFieldId }));
                this.store.dispatch(sprayApplicationCreated());
              }),
              map(() => cropFieldId),
            );
          }),
          toArray(),
          map(() => loadSprayApplications({ year: action.sprayApplication.cropYear })),
        ),
      ),
    ),
  );

  deleteSprayApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteSprayApplication),
      tap(() => this.store.dispatch(setIsSpraysLoading({ isSpraysLoading: true }))),
      switchMap(({ sprayApplicationId, year, cropFieldId }) =>
        this.blightService.deleteSprayApplication(sprayApplicationId, cropFieldId, year).pipe(
          tap(() => this.store.dispatch(addShouldRecalculate({ cropFieldId }))),
          map(() => loadSprayApplications({ year })),
        ),
      ),
    ),
  );


  loadSprayApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSprayApplications),
      withLatestFrom(this.store.pipe(select(selectSelectedBlightCropFieldIds))),
      tap(() => this.store.dispatch(setIsSpraysLoading({ isSpraysLoading: true }))),
      switchMap(([{ payload: { year } }, selectedIds]) =>
        this.blightService.getSprayApplications(selectedIds, year).pipe(
          tap(() => this.store.dispatch(setIsSpraysLoading({ isSpraysLoading: false }))),
          map((sprayApplications: SprayApplication[]) => loadSprayApplicationsSuccess({ sprayApplications })),
          catchError(() =>
            of(loadSprayApplicationsFailure({ message: 'errors.blight.failedToLoadBasicSprays' }))),
        ),
      ),
    ),
  );

  adjustSprayType$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openSpraysDialog),
        switchMap(({ sprayType, advices, actionDate, readOnly }) => {
          this.store.dispatch(setSelectedBlightCropFieldIds({ cropFieldIds: advices.map((bd) => bd.cropFieldId) }));
          const cropYear = advices[0]?.cropYear;
          if (cropYear) {
            this.store.dispatch(loadParticipatingCropFields({ cropYear }));
          }
          const dialogTitle = this.locale.translate('blight.adjustSprayDialogTitle');
          return this.dialog
            .fullscreenDialog(
              AdjustSpraysDialogComponent,
              {
                title: dialogTitle,
                sprayType,
                advices,
                defaultDate: actionDate,
                readOnly: readOnly ?? false,
              },
              {
                id: 'sprayDialog',
                maxWidth: '80vw',
              },
            )
            .pipe(
              withLatestFrom(this.store.pipe(select(selectShouldRecalculateForCropFieldIds))),
              switchMap(([_, shouldRecalculateIds]) =>
                shouldRecalculateIds.length > 0
                  ? shouldRecalculateIds.map((cropFieldId) =>
                    of(this.store.dispatch(requestBlightRecalculation({ cropFieldId, year: advices[0].cropYear }))),
                  )
                  : EMPTY,
              ),
            );
        }),
      ),
    { dispatch: false },
  );

  // TODO: Remove from effects
  openBlightIrrigationDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(openBlightIrrigation),
        switchMap(({ advices, actionDate }) => {
          this.store.dispatch(setSelectedBlightCropFieldIds({ cropFieldIds: advices.map((bd) => bd.cropFieldId) }));
          const cropYear = advices[0]?.cropYear;
          const readOnly = cropYear != null && isCropYearReadOnly(cropYear, this.dateService.currentYear());
          if (cropYear) {
            this.store.dispatch(loadParticipatingCropFields({ cropYear }));
          }
          const dialogTitle = this.locale.translate('irrigation.dialogTitle');
          return this.dialog
            .fullscreenDialog(IrrigationDialogComponent, {
              title: dialogTitle,
              selectedCropFields: advices.map((bd) => ({
                id: bd.cropFieldId,
                name: bd.cropFieldName,
                areaInHectare: bd.amountOfHectares,
              })),
              actionDate: actionDate ?? new Date(),
              readOnly,
            })
            .pipe(
              withLatestFrom(this.store.pipe(select(selectShouldRecalculateForCropFieldIds))),
              switchMap(([_, shouldRecalculateIds]) =>
                shouldRecalculateIds.length > 0
                  ? shouldRecalculateIds.map((cropFieldId) =>
                    of(this.store.dispatch(requestBlightRecalculation({ cropFieldId, year: advices[0]?.cropYear }))),
                  )
                  : EMPTY,
              ),
            );
        }),
      ),
    { dispatch: false },
  );


  requestBlightRecalculation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(requestBlightRecalculation),
        groupBy((action) => action.cropFieldId),
        mergeMap((group$) =>
          group$.pipe(
            debounceTime(500),
            mergeMap(({ cropFieldId, year }) =>
              this.blightService.recalculateBlightData(cropFieldId, year).pipe(
                tap(() => {
                  this.store.dispatch(
                    setBlightRecalculationStatusPerCropField({
                      blightRecalculationStatusPerCropField: {
                        isBlightRecalculating: true,
                        cropFieldId,
                      },
                    }));
                  this.store.dispatch(removeShouldRecalculate({ cropFieldId }));
                }),
                catchError(() =>
                  of(
                    NotificationActions.errorSnackbar({
                      message: this.locale.translate('errors.blight.failedToRecalculate'),
                    }),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
    { dispatch: false },
  );

  loadCropFieldRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCropFieldRegistration),
      tap(() => this.store.dispatch(setIsSpraysLoading({ isSpraysLoading: true }))),
      switchMap((action) =>
        this.blightService.getCropFieldRegistration(action.cropFieldId, action.year).pipe(
          map((cropFieldRegistration: CropFieldRegistration) => setCropFieldRegistration({ cropFieldRegistration })),
          catchError(() =>
            of(
              NotificationActions.errorSnackbar({
                message: this.locale.translate('errors.blight.failedToLoadBasicSprays'),
              }),
            ),
          ),
        ),
      ),
    ),
  );


  updateBlightPlantDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePlantDate),
      switchMap(({ cropFieldId, date }) =>
        this.blightService
          .updatePlantDate(cropFieldId, date)
          .pipe(
            switchMap((cropFieldRegistration: CropFieldRegistration) =>
              of(plantDateUpdated({ cropFieldRegistration })),
            ),
            catchError((error: HttpErrorResponse) =>
              handleHttpErrorWithSnackbar(
                error,
                'errors.blight.genericError',
                this.locale,
                'BFP.Validation.Backend',
              ),
            ),
          ),
      ),
    ),
  );

  plantDateUpdatedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(plantDateUpdated),
        switchMap(({ cropFieldRegistration }) =>
          of(
            NotificationActions.successSnackbar({
              message: this.locale.translate('BFP.Notifications.PlantDateUpdated', {
                plantDate: new Date(cropFieldRegistration.plantDate).toLocaleDateString(this.locale.getLocale()),
              }),
            }),
          ),
        ),
      ),
  );


  updateBlightEmergenceDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateEmergenceDate),
      switchMap(({ date, cropFieldId }) =>
        this.blightService
          .updateEmergenceDate(cropFieldId, date)
          .pipe(
            switchMap((cropFieldRegistration: CropFieldRegistration) =>
              of(emergenceDateUpdated({ cropFieldRegistration })),
            ),
            catchError((error: HttpErrorResponse) =>
              handleHttpErrorWithSnackbar(
                error,
                'errors.blight.genericError',
                this.locale,
                'BFP.Validation.Backend',
              ),
            ),
          ),
      ),
    ),
  );

  emergenceDateUpdatedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(emergenceDateUpdated),
        switchMap(({ cropFieldRegistration }) =>
          of(
            NotificationActions.successSnackbar({
              message: this.locale.translate('BFP.Notifications.EmergenceDateUpdated', {
                emergenceDate: new Date(cropFieldRegistration.emergenceDate).toLocaleDateString(this.locale.getLocale()),
              }),
            }),
          ),
        ),
      ),
  );


  updateBlightHarvestDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateHarvestDate),
      switchMap(({ date, cropFieldId }) =>
        this.blightService
          .updateHarvestDate(cropFieldId, date)
          .pipe(
            switchMap((cropFieldRegistration: CropFieldRegistration) =>
              of(harvestDateUpdated({ cropFieldRegistration })),
            ),
            catchError((error: HttpErrorResponse) =>
              handleHttpErrorWithSnackbar(
                error,
                'errors.blight.genericError',
                this.locale,
                'BFP.Validation.Backend',
              ),
            ),
          ),
      ),
    ),
  );

  harvestDateUpdatedSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(harvestDateUpdated),
        switchMap(({ cropFieldRegistration }) =>
          of(
            NotificationActions.successSnackbar({
              message: this.locale.translate('BFP.Notifications.HarvestDateUpdated', {
                harvestDate: new Date(cropFieldRegistration.harvestDate).toLocaleDateString(this.locale.getLocale()),
              }),
            }),
          ),
        ),
      ),
  );

  updateSuppressSprayAdviceUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateSuppressSprayAdvice),
      switchMap(({ year, cropFieldId, suppressSprayAdvice }) =>
        this.blightService
          .updateSuppressSprayAdvice(cropFieldId, year, suppressSprayAdvice)
          .pipe(switchMap((_) => of(requestBlightRecalculation({ cropFieldId, year })))),
      ),
    ),
  );

  updatePhytophthoraPressureDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updatePhytophthoraPressure),
      switchMap(({ year, cropFieldId, phytophthoraPressure }) =>
        this.blightService
          .updatePhytophthoraPressure(cropFieldId, year, phytophthoraPressure)
          .pipe(map(() => requestBlightRecalculation({ cropFieldId, year }))),
      ),
    ),
  );

  updateCropFieldVisibilityStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCropFieldVisibilityStatus),
      switchMap(({ cropFieldId, isVisible }) =>
        this.blightService
          .updateCropFieldVisibilityStatus(cropFieldId, new Date().getFullYear(), isVisible)
          .pipe(map(() => updateCropFieldVisibilityStatusSuccess({
            cropFieldId,
            isVisible,
          }))),
      ),
    ),
  );

  getLegalRestrictions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadLegalRestrictions),
      switchMap(({ payload: { cropAndCropAids } }) =>
        this.cropVarietyService.getLegalRestrictions(cropAndCropAids).pipe(
          map((legalRestrictions: LegalRestriction[]) => loadLegalRestrictionsSuccess({ legalRestrictions })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
      catchError(() =>
        of(loadLegalRestrictionsFailure({ message: 'errors.getLegalRestrictionsErrorMessage' })),
      ),
    ),
  );

  loadCropFieldsForSpray$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCropFieldsForSpray),
      switchMap(({ date }) =>
        this.blightService.getCropFieldsForSprayApplications(date).pipe(map((cropFieldIds: number[]) => setCropFieldsForSpray({ cropFieldIds }))),
      ),
    ),
  );

  handleError$ = createEffect(
    () => this.actions$.pipe(
      ofType(
        loadBlightDataFailure,
        loadCustomerBlightRightsFailure,
      ),
      map(({ payload: { message } }) => {
        return NotificationActions.errorSnackbar({
          message: this.locale.translate(message),
        })
      }),
    ),
  );

  checkUnsavedChanges$ = createEffect(
    () => this.actions$.pipe(
      ofType(checkUnsavedChanges),
      switchMap(({ targetUrl }) => {
        return this.dialog
          .confirm({
            dialogCancelDefault: true,
            title: this.locale.translate('TGA.UnsavedChanges.Title'),
            confirmButtonText: this.locale.translate('TGA.UnsavedChanges.Leave'),
            confirmButtonIcon: false,
            cancelButtonText: this.locale.translate('TGA.UnsavedChanges.Stay'),
            text: this.locale.translate('TGA.UnsavedChanges.Message'),
            dismissAtClose: true,
          })
          .pipe(
            switchMap((result) => {
              if (result.status === DIALOG_RETURN_TYPES.confirm) {
                return of(confirmLeaveWithUnsavedChanges({ targetUrl }));
              } else {
                return EMPTY;
              }
            }),
            catchError(() => {
              return of(confirmLeaveWithUnsavedChanges({ targetUrl }));
            }),
          );
      }),
    ),
  );

  confirmLeaveWithUnsavedChanges$ = createEffect(
    () => this.actions$.pipe(
      ofType(confirmLeaveWithUnsavedChanges),
      tap(({ targetUrl }) => {
        this.unsavedChangesService.unregisterForm();
        this.router.navigateByUrl(targetUrl);
      }),
    ),
    { dispatch: false },
  );
}
